/**
 * Journey Workflow Tab with revision selector + read-only past-revision view.
 *
 * - RevisionSelector rendered in the builder header area.
 * - Selecting a past revision fetches that revision's steps via
 *   JourneyService.fetchRevisionSteps() and remounts JourneysBuilder keyed on the
 *   revision id with mode=READONLY.
 * - Amber lock banner + "Back to current revision" exit in read-only mode.
 * - Emits the currently-viewed revision id via onRevisionChange (null = active).
 */

import React from 'react'
import { BetterComponent } from '../../../components/better-component/better-component'
import { AppState } from '../../../stores/app'
import { NotificationService, JourneyService } from '../../../services'
import { Container } from 'typescript-ioc/es5'
import './journey-revisions.scss'
import clone from 'clone'
import { StepType } from '@pushly/models/lib/enums/journeys'
import { Loading3QuartersOutlined, LockOutlined, ArrowLeftOutlined } from '@ant-design/icons'
import { Button as AntButton } from 'antd'
import { ApiVersion } from '../../../enums/api-version.enum'
import { JourneysBuilder } from 'src/features/journeys/journeys-builder'
import { JourneyBuilderMode } from '../enums/journey-builder-mode.enum'
import type { Journey, JourneyRevision } from '../types/journey-api-response'
import type { IJourneyStep } from '@pushly/models/lib/structs/journeys'
import {
    buildRevisionMetaList,
    VariantSplitConfiguration,
} from '../journey-notifications/revision-insights'
import { RevisionSelector } from './revision-selector'

interface IJourneyWorkflowTabProps {
    journey: Journey
    /** The active-tab descriptor from the parent TabbedView (read as a truthiness gate). */
    activeTab: unknown
    /**
     * Called with the currently-viewed revision id (null = active revision) so a
     * consumer (e.g. the Insights tab) can pre-scope itself.
     */
    onRevisionChange?: (revisionId: number | null) => void
}

interface IJourneyWorkflowTabState {
    loading: boolean
    initialStateLoaded: boolean
    /** Full journey object with active-revision steps enriched for display. */
    journey: Journey
    /** The currently-selected revision id. null = active revision. */
    selectedRevisionId: number | null
    /** The loaded past-revision (when selectedRevisionId is non-null). */
    pastRevision: JourneyRevision | null
    /** Loading spinner shown while fetching a past revision. */
    pastRevisionLoading: boolean
}

export class JourneyWorkflowTab extends BetterComponent<IJourneyWorkflowTabProps, IJourneyWorkflowTabState> {
    protected static tabName = 'workflow'
    protected static tabLabel = 'Workflow'

    private appState: AppState
    private notifService: NotificationService
    private journeyService: JourneyService

    public constructor(props: IJourneyWorkflowTabProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.notifService = Container.get(NotificationService)
        this.journeyService = Container.get(JourneyService)

        this.state = {
            loading: true,
            initialStateLoaded: false,
            journey: this.props.journey,
            selectedRevisionId: null,
            pastRevision: null,
            pastRevisionLoading: false,
        }
    }

    public async componentDidMount() {
        const journey = clone(this.props.journey)
        if (this.props.journey) {
            const steps = await this.attachActionParams(journey.revision.steps)

            journey.steps = steps
            journey.revision.steps = steps

            // On mount we show the active revision; emit null (= active).
            this.props.onRevisionChange?.(null)

            return this.setState({ journey, initialStateLoaded: true, loading: false })
        } else {
            return this.setState({ initialStateLoaded: true, loading: false })
        }
    }

    public render(): React.ReactNode {
        const { journey, selectedRevisionId, pastRevision, pastRevisionLoading, initialStateLoaded } = this.state

        // Build revision metadata for the selector
        const revisionMetas = buildRevisionMetaList(journey, this.appState.currentDomain?.timezone)

        // Promoted variant key for each revision (for "promoted '…'" note in dropdown)
        const promotedVariantLabelByRevisionId = this.buildPromotedVariantLabels()

        // Are we viewing a past revision?
        const isViewingPastRevision = selectedRevisionId !== null && pastRevision !== null

        // Build the journey object to pass to the builder (past revision overrides active steps)
        const builderJourney: Journey = isViewingPastRevision
            ? { ...journey, revision: pastRevision, steps: pastRevision.steps }
            : journey

        // Key the builder on BOTH revision id and whether the past revision has loaded.
        // This is critical: when the user selects a past revision, the key must change
        // once the past revision's steps are available so the builder remounts and reads
        // the correct steps from journey.revision.steps.
        const builderKey = isViewingPastRevision
            ? `revision-${selectedRevisionId}-loaded`
            : selectedRevisionId !== null
            ? `revision-${selectedRevisionId}-loading`
            : this.props.activeTab
            ? `revision-active-${journey.revision?.id ?? 'current'}`
            : 'hidden'

        // RevisionSelector floated right in the Builder header row.
        // Only render when the journey has revision history (single or multi).
        const headerActions = revisionMetas.length > 0 ? (
            <RevisionSelector
                revisions={revisionMetas}
                selectedRevisionId={selectedRevisionId}
                onSelect={this.handleRevisionSelect}
                promotedVariantLabelByRevisionId={promotedVariantLabelByRevisionId}
            />
        ) : undefined

        // Amber read-only callout inside the builder, under the header: the lock
        // banner text plus the right-aligned "Back to current revision" button.
        const pastMeta = revisionMetas.find((m) => m.id === selectedRevisionId)
        const bodyHeader = isViewingPastRevision ? (
            <div
                className="journey-workflow-tab-past-revision-banner"
                data-testid="past-revision-banner"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 11,
                    padding: '11px 20px',
                    background: '#fff7e6',
                    borderTop: '1px solid #ffd591',
                    borderBottom: '1px solid #ffd591',
                }}
            >
                <LockOutlined style={{ color: '#d48806', fontSize: 17, flexShrink: 0 }} />
                <div style={{ flex: 1, fontSize: 13, color: '#874d00', lineHeight: 1.4 }}>
                    <strong>
                        Viewing a past revision{pastMeta ? ` (Revision ${pastMeta.ordinal})` : ''} — read only.
                    </strong>{' '}
                    This is how the journey ran at the time. Editing is disabled here.
                </div>
                <AntButton
                    type="primary"
                    size="small"
                    icon={<ArrowLeftOutlined />}
                    onClick={this.handleBackToCurrent}
                    data-testid="back-to-current-btn"
                    style={{
                        background: 'var(--color-secondary-100)',
                        borderColor: 'var(--color-secondary-100)',
                        fontWeight: 600,
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                    }}
                >
                    Back to current revision
                </AntButton>
            </div>
        ) : undefined

        return (
            <div
                className={`journey-workflow-tab${
                    isViewingPastRevision ? ' journey-workflow-tab--viewing-past' : ''
                }`}
                style={{ height: '100%', minHeight: 600 }}
            >
                {(pastRevisionLoading || (!initialStateLoaded)) ? (
                    <Loading3QuartersOutlined
                        spin={true}
                        style={{ fontSize: '24px', position: 'relative', top: '275px', left: '50%' }}
                    />
                ) : (
                    <JourneysBuilder
                        key={builderKey}
                        loading={this.isLoading}
                        mode={JourneyBuilderMode.READONLY}
                        domain={this.appState.currentDomain!}
                        journey={builderJourney}
                        headerActions={headerActions}
                        bodyHeader={bodyHeader}
                        promotionDisabled={isViewingPastRevision}
                    />
                )}
            </div>
        )
    }

    public get isLoading() {
        return this.state.loading || !this.state.initialStateLoaded || this.state.pastRevisionLoading
    }

    // ─── Revision selection ─────────────────────────────────────────────────

    protected handleRevisionSelect = async (revisionId: number | null) => {
        if (revisionId === null) {
            // Back to active revision
            await this.setState({ selectedRevisionId: null, pastRevision: null })
            // Emit null to signal Insights tab: no specific past-revision selected
            this.props.onRevisionChange?.(null)
            return
        }

        // Check if we're already on this revision
        if (revisionId === this.state.selectedRevisionId) return

        // Fetch the past revision's steps
        await this.setState({ pastRevisionLoading: true, selectedRevisionId: revisionId })

        const res = await this.journeyService.fetchRevisionSteps(this.state.journey.id, revisionId, {
            showLoadingScreen: false,
            cancellationKey: `workflow-tab.fetchRevisionSteps.${revisionId}`,
        })

        if (res.ok && res.data) {
            const fetchedRevision = res.data

            // Enrich action steps' params.notification for display (same as active revision)
            const enrichedSteps = await this.attachActionParams(fetchedRevision.steps ?? [])
            const enrichedRevision: JourneyRevision = { ...fetchedRevision, steps: enrichedSteps }

            await this.setState({
                pastRevision: enrichedRevision,
                pastRevisionLoading: false,
            })

            // Emit the actual past-revision id so a consumer can scope to it.
            this.props.onRevisionChange?.(revisionId)
        } else {
            // Fetch failed — revert to active revision
            await this.setState({ selectedRevisionId: null, pastRevision: null, pastRevisionLoading: false })
        }
    }

    protected handleBackToCurrent = () => {
        this.handleRevisionSelect(null)
    }

    // ─── Promoted variant labels ────────────────────────────────────────────

    /**
     * Build a map from revision_id → promoted variant label (branch.label / display name)
     * for the "promoted '…'" note in the revision selector dropdown.
     *
     * We look up the branch.label (not the branch.key) for human-readable display.
     */
    protected buildPromotedVariantLabels(): Map<number, string> {
        const map = new Map<number, string>()
        const revisions = this.state.journey?.revisions ?? []

        for (const rev of revisions) {
            const steps: IJourneyStep[] = rev.steps ?? []
            for (const step of steps) {
                if (step.type === StepType.VARIANT_SPLIT) {
                    const cfg = step.configuration as VariantSplitConfiguration | undefined
                    const key = cfg?.promoted_variant_key
                    if (key != null && key !== '') {
                        // Find the branch with this key and use its label for display
                        const branch = (cfg?.branches ?? []).find((b) => String(b.key) === String(key))
                        const displayLabel = branch?.label ?? String(key)
                        map.set(rev.id, displayLabel)
                    }
                }
            }
        }

        return map
    }

    // ─── Action params enrichment ───────────────────────────────────────────

    protected attachActionParams = async (steps: IJourneyStep[]) => {
        let modifiedSteps = clone(steps)

        if (Array.isArray(modifiedSteps) && modifiedSteps.length > 0) {
            const resolvers = Promise.all(
                modifiedSteps.map(async (step) => {
                    const params = step.configuration?.params
                    const notificationId = params && 'notification_id' in params ? params.notification_id : undefined
                    if (!notificationId || step.type !== StepType.ACTION) {
                        return step
                    }

                    const query = {
                        include_schedules: false,
                    }
                    const notif = await this.notifService.fetchNotificationById(
                        this.appState.currentDomain!.id,
                        notificationId,
                        { query, version: ApiVersion.V4 },
                    )

                    if (notif) {
                        const params = step.configuration.params
                        if (params) {
                            params.notification = notif
                        }
                    }

                    return step
                }),
            )

            modifiedSteps = await resolvers
        }

        return modifiedSteps
    }
}
